import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { motion } from 'framer-motion'
import HeadphonesRoundedIcon from '@mui/icons-material/HeadphonesRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded'
import ShieldMoonRoundedIcon from '@mui/icons-material/ShieldMoonRounded'
import WallpaperRoundedIcon from '@mui/icons-material/WallpaperRounded'
import type { CosmeticRarity } from '../../types/schema'
import { FOCUS_GUARDIANS, RARITY_BORDER_COLORS } from '../../utils/gamificationRegistry'
import {
  AUDIO_TRACKS,
  AVATARS,
  BACKGROUNDS,
  DEFAULT_UNLOCKS,
  RARITY_UNLOCK_LEVEL,
  THEMES,
  ORBIT_SKINS,
  TITLE_PREFIXES,
  TITLE_SUFFIXES,
  type AvatarItem,
  type CosmeticItem,
} from '../../utils/cosmeticCatalog'
import { useGamification } from '../../context/useGamification'
import { usePreview } from '../../context/usePreview'
import type { PreviewKind } from '../../context/previewContextCore'
import { isNoSignInPreviewActive } from '../../debug'
import { DisplayCard, type DisplayCardRarity, type DisplayCardState } from '../Common'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import { useNls } from '../../nls/nlsProvider'
import { getRarityLabel, type ExperienceNlsT } from '../ExperienceNls'

type SlotUnlockField = 'unlockedThemeIds' | 'unlockedBackgroundIds' | 'unlockedOrbitSkinIds'

type HighlightTarget = {
  kind: 'THEME' | 'BACKGROUND' | 'SOUNDSCAPE' | 'ORBIT'
  cosmeticId: string
}

type CharacterPageProps = {
  highlightTarget?: HighlightTarget | null
  onHighlightConsumed?: () => void
}

const RARITY_MOD: Record<CosmeticRarity, string> = {
  COMMON: 'is-common',
  RARE: 'is-rare',
  EPIC: 'is-epic',
  LEGENDARY: 'is-legendary',
}

const toDisplayRarity = (rarity: CosmeticRarity): DisplayCardRarity => rarity.toLowerCase() as DisplayCardRarity

const rarityAccent = (rarity: CosmeticRarity): CSSProperties =>
  ({
    ['--char-accent' as string]: RARITY_BORDER_COLORS[rarity],
    ['--card-accent' as string]: RARITY_BORDER_COLORS[rarity],
  })

/**
 * Returns `true` for `ms` milliseconds after `active` transitions from
 * false → true. Used to fire a one-shot equip flash without keeping any
 * looping keyframe alive.
 */
const useJustBecame = (active: boolean, ms = 700): boolean => {
  const [flag, setFlag] = useState(false)
  const prev = useRef(active)
  useEffect(() => {
    if (active && !prev.current) {
      setFlag(true)
      const timer = window.setTimeout(() => setFlag(false), ms)
      prev.current = active
      return () => window.clearTimeout(timer)
    }
    prev.current = active
    return undefined
  }, [active, ms])
  return flag
}

const RarityTag = ({ rarity, t }: { rarity: CosmeticRarity; t: ExperienceNlsT }) => (
  <span className={`charRarityTag ${RARITY_MOD[rarity]}`}>{getRarityLabel(rarity, t)}</span>
)

const parseEquippedTitle = (equippedTitle?: string) => {
  const trimmed = equippedTitle?.trim()
  if (trimmed) {
    const prefix = [...TITLE_PREFIXES]
      .sort((a, b) => b.length - a.length)
      .find((candidate) => trimmed.startsWith(`${candidate} `))
    if (prefix) {
      const suffix = trimmed.slice(prefix.length).trim()
      if (TITLE_SUFFIXES.includes(suffix)) {
        return { prefix, suffix }
      }
    }
  }
  return {
    prefix: DEFAULT_UNLOCKS.titlePrefixes[0],
    suffix: DEFAULT_UNLOCKS.titleSuffixes[0],
  }
}

// ---------------------------------------------------------------------------
// Identity Spotlight (hero)
// ---------------------------------------------------------------------------

const IdentitySpotlight = ({
  avatar,
  equippedTitle,
  level,
  themeName,
  backgroundName,
  audioName,
  t,
}: {
  avatar: AvatarItem | undefined
  equippedTitle: string
  level: number
  themeName: string
  backgroundName: string
  audioName: string
  t: ExperienceNlsT
}) => {
  const rarity = avatar?.rarity ?? 'COMMON'
  return (
    <motion.section
      className="charHero"
      style={rarityAccent(rarity)}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="charHeroInner">
        <div className={`charHeroAvatar ${RARITY_MOD[rarity]}`} aria-hidden="true">
          <span className="charHeroAvatarGlyph">{avatar?.emoji ?? '🌱'}</span>
        </div>
        <div className="charHeroCore">
          <span className="charHeroEyebrow">{t('NLSExperienceIdentityLoadout')}</span>
          <h2 className="charHeroName">{avatar?.name ?? 'Seedling'}</h2>
          <div className="charHeroMeta">
            <span className="charHeroChip charHeroChip--level">{t('NLSExperienceLevelAbbrev')} {level}</span>
            <span className="charHeroChip charHeroChip--title">{equippedTitle}</span>
            <span className={`charHeroChip ${RARITY_MOD[rarity]}`}>{getRarityLabel(rarity, t)}</span>
          </div>
        </div>
        <ul className="charHeroLoadout" aria-label={t('NLSExperienceEquippedLoadoutAria')}>
          <li>
            <span className="charLoadoutIcon"><PaletteRoundedIcon fontSize="inherit" /></span>
            <span className="charLoadoutLabel">{t('NLSExperienceTheme')}</span>
            <strong>{themeName}</strong>
          </li>
          <li>
            <span className="charLoadoutIcon"><WallpaperRoundedIcon fontSize="inherit" /></span>
            <span className="charLoadoutLabel">{t('NLSExperienceCanvas')}</span>
            <strong>{backgroundName}</strong>
          </li>
          <li>
            <span className="charLoadoutIcon"><HeadphonesRoundedIcon fontSize="inherit" /></span>
            <span className="charLoadoutLabel">{t('NLSExperienceSoundscape')}</span>
            <strong>{audioName}</strong>
          </li>
        </ul>
      </div>
    </motion.section>
  )
}

// ---------------------------------------------------------------------------
// Guardian card
// ---------------------------------------------------------------------------

type ActionRowProps = {
  isEquipped: boolean
  isOwned: boolean
  canUnlock: boolean
  lockedLabel: string
  isBusy: boolean
  previewBusy: boolean
  isPreviewingThis: boolean
  onEquip: () => void
  onUnlock: () => void
  onPreview: () => void
  t: ExperienceNlsT
}

const ActionRow = ({
  isEquipped,
  isOwned,
  canUnlock,
  lockedLabel,
  isBusy,
  previewBusy,
  isPreviewingThis,
  onEquip,
  onUnlock,
  onPreview,
  t,
}: ActionRowProps) => {
  if (isEquipped) return <span className="charEquippedTag">{t('NLSExperienceEquipped')}</span>
  if (isOwned) {
    return (
      <Button variant="secondary" size="sm" disabled={isBusy} onClick={onEquip}>
        {t('NLSExperienceEquip')}
      </Button>
    )
  }
  if (canUnlock) {
    return (
      <Button variant="primary" size="sm" className="charUnlockCta" disabled={isBusy} onClick={onUnlock}>
        {t('NLSExperienceUnlock')}
      </Button>
    )
  }
  return (
    <div className="charLockedRow">
      <span className="charLockedTag"><LockRoundedIcon fontSize="inherit" /> {lockedLabel}</span>
      <Button
        variant="tertiary"
        size="sm"
        className="charPreviewCta"
        disabled={previewBusy}
        onClick={onPreview}
      >
        {isPreviewingThis ? t('NLSExperiencePreviewing') : t('NLSExperiencePreview')}
      </Button>
    </div>
  )
}

const GuardianCard = ({
  avatar,
  isOwned,
  isEquipped,
  canUnlock,
  isBusy,
  previewBusy,
  isPreviewingThis,
  onEquip,
  onUnlock,
  onPreview,
  t,
}: {
  avatar: AvatarItem
  isOwned: boolean
  isEquipped: boolean
  canUnlock: boolean
  isBusy: boolean
  previewBusy: boolean
  isPreviewingThis: boolean
  onEquip: () => void
  onUnlock: () => void
  onPreview: () => void
  t: ExperienceNlsT
}) => {
  const guardian = avatar.guardianId ? FOCUS_GUARDIANS[avatar.guardianId] : null
  const justEquipped = useJustBecame(isEquipped)
  const states: DisplayCardState[] = [
    isEquipped ? 'equipped' : 'default',
    !isOwned && !canUnlock ? 'locked' : 'default',
  ]
  return (
    <DisplayCard
      variant="chip"
      rarity={toDisplayRarity(avatar.rarity)}
      state={states}
      className={`${RARITY_MOD[avatar.rarity]} ${justEquipped ? 'is-just-equipped' : ''}`}
      style={rarityAccent(avatar.rarity)}
      icon={(
        <div className="charGuardianGlyphWrap" aria-hidden="true">
        <span className="charGuardianGlyph">{avatar.emoji}</span>
      </div>
      )}
      title={<span className="charGuardianName">{avatar.name}</span>}
      badge={<RarityTag rarity={avatar.rarity} t={t} />}
      body={(
        <>
          <p className="charGuardianDesc">{avatar.description}</p>
          {guardian ? (
            <span className="charGuardianBundle" title={t('NLSExperienceGuardianBundleTitle')}>
              <ShieldMoonRoundedIcon fontSize="inherit" /> {t('NLSExperienceGuardianBundles', { label: guardian.audioLabel })}
            </span>
          ) : null}
        </>
      )}
      footer={(
        <ActionRow
          isEquipped={isEquipped}
          isOwned={isOwned}
          canUnlock={canUnlock}
          lockedLabel={`Lv ${RARITY_UNLOCK_LEVEL[avatar.rarity]}`}
          isBusy={isBusy}
          previewBusy={previewBusy}
          isPreviewingThis={isPreviewingThis}
          onEquip={onEquip}
          onUnlock={onUnlock}
          onPreview={onPreview}
          t={t}
        />
      )}
    />
  )
}

// ---------------------------------------------------------------------------
// Environment (Themes + Backgrounds) & Soundscape strips
// ---------------------------------------------------------------------------

const SlotCard = ({
  item,
  isOwned,
  isEquipped,
  canUnlock,
  isBusy,
  previewBusy,
  isPreviewingThis,
  lockedLabel,
  onEquip,
  onUnlock,
  onPreview,
  registerRef,
  isHighlighted,
  variant,
  t,
}: {
  item: CosmeticItem
  isOwned: boolean
  isEquipped: boolean
  canUnlock: boolean
  isBusy: boolean
  previewBusy: boolean
  isPreviewingThis: boolean
  lockedLabel: string
  onEquip: () => void
  onUnlock: () => void
  onPreview: () => void
  registerRef: (id: string) => (el: HTMLElement | null) => void
  isHighlighted: boolean
  variant: 'theme' | 'background' | 'audio' | 'orbit'
  t: ExperienceNlsT
}) => {
  const justEquipped = useJustBecame(isEquipped)
  return (
    <DisplayCard
      ref={registerRef(item.id)}
      variant="showcase"
      rarity={toDisplayRarity(item.rarity)}
      state={isEquipped ? 'equipped' : undefined}
      className={`charSlotVariant--${variant} ${RARITY_MOD[item.rarity]} ${justEquipped ? 'is-just-equipped' : ''} ${isHighlighted ? 'charHighlightPulse' : ''}`}
      style={rarityAccent(item.rarity)}
      media={<div className={`charSlotSwatch charSlotSwatch--${variant}`} data-id={item.id} aria-hidden="true" />}
      title={<span className="charSlotName">{item.name}</span>}
      badge={<RarityTag rarity={item.rarity} t={t} />}
      body={<p className="charSlotDesc">{item.description}</p>}
      footer={(
        <ActionRow
          isEquipped={isEquipped}
          isOwned={isOwned}
          canUnlock={canUnlock}
          lockedLabel={lockedLabel}
          isBusy={isBusy}
          previewBusy={previewBusy}
          isPreviewingThis={isPreviewingThis}
          onEquip={onEquip}
          onUnlock={onUnlock}
          onPreview={onPreview}
          t={t}
        />
      )}
    />
  )
}

// ---------------------------------------------------------------------------
// Title Forge
// ---------------------------------------------------------------------------

const TitleForge = ({
  prefix,
  suffix,
  ownedPrefixes,
  ownedSuffixes,
  isBusy,
  onPrefix,
  onSuffix,
  onEquip,
  t,
}: {
  prefix: string
  suffix: string
  ownedPrefixes: Set<string>
  ownedSuffixes: Set<string>
  isBusy: boolean
  onPrefix: (value: string) => void
  onSuffix: (value: string) => void
  onEquip: () => void
  t: ExperienceNlsT
}) => {
  const [flashKey, setFlashKey] = useState(0)
  const canEquip = ownedPrefixes.has(prefix) && ownedSuffixes.has(suffix)
  return (
    <section className="charForge">
      <div className="charForgeSelectors">
        <div className="charForgeColumn">
          <span className="charForgeLegend">{t('NLSExperiencePrefix')}</span>
          <div className="charForgePillList" role="radiogroup" aria-label={t('NLSExperienceTitlePrefixAria')}>
            {TITLE_PREFIXES.map((option) => {
              const isOwned = ownedPrefixes.has(option)
              const isActive = option === prefix
              return (
                <Pressable
                  key={option}
                  role="radio"
                  aria-checked={isActive}
                  className={`charForgePill ${isActive ? 'is-active' : ''} ${isOwned ? '' : 'is-locked'}`}
                  disabled={!isOwned}
                  onClick={() => {
                    if (!isOwned) return
                    onPrefix(option)
                    setFlashKey((k) => k + 1)
                  }}
                >
                  {option}
                  {!isOwned && <LockRoundedIcon fontSize="inherit" />}
                </Pressable>
              )
            })}
          </div>
        </div>
        <div className="charForgeColumn">
          <span className="charForgeLegend">{t('NLSExperienceSuffix')}</span>
          <div className="charForgePillList" role="radiogroup" aria-label={t('NLSExperienceTitleSuffixAria')}>
            {TITLE_SUFFIXES.map((option) => {
              const isOwned = ownedSuffixes.has(option)
              const isActive = option === suffix
              return (
                <Pressable
                  key={option}
                  role="radio"
                  aria-checked={isActive}
                  className={`charForgePill ${isActive ? 'is-active' : ''} ${isOwned ? '' : 'is-locked'}`}
                  disabled={!isOwned}
                  onClick={() => {
                    if (!isOwned) return
                    onSuffix(option)
                    setFlashKey((k) => k + 1)
                  }}
                >
                  {option}
                  {!isOwned && <LockRoundedIcon fontSize="inherit" />}
                </Pressable>
              )
            })}
          </div>
        </div>
      </div>
      <div className="charForgePreviewPanel">
        <span className="charForgeLegend">{t('NLSExperiencePreview')}</span>
        <motion.strong
          key={flashKey}
          className="charForgePreviewText"
          initial={{ opacity: 0.4, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          {prefix} {suffix}
        </motion.strong>
        <Button
          variant="primary"
          size="sm"
          className="charForgeEquip"
          disabled={isBusy || !canEquip}
          onClick={onEquip}
        >
          {t('NLSExperienceForgeTitle')}
        </Button>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const CharacterPage = ({ highlightTarget, onHighlightConsumed }: CharacterPageProps) => {
  const { t } = useNls()
  const { profile, isLoading, equip, unlock } = useGamification()
  const { activePreview, startPreview } = usePreview()
  const [busyId, setBusyId] = useState<string | null>(null)
  const [highlightedId, setHighlightedId] = useState<string | null>(null)

  const itemRefs = useRef(new Map<string, HTMLElement>())
  const registerItemRef = (id: string) => (el: HTMLElement | null) => {
    if (el) itemRefs.current.set(id, el)
    else itemRefs.current.delete(id)
  }

  const level = profile?.level ?? 1
  const isDebugPreview = isNoSignInPreviewActive()
  const previewBusy = !!activePreview
  const activePreviewCosmeticId = activePreview?.cosmeticId ?? null

  const handlePreview = (item: CosmeticItem, kind: PreviewKind) => {
    startPreview({ kind, cosmeticId: item.id, label: item.name })
  }

  const owned = useMemo(() => {
    const avatars = new Set<string>(isDebugPreview ? AVATARS.map((avatar) => avatar.id) : [...DEFAULT_UNLOCKS.avatars, ...(profile?.unlockedAvatarIds ?? [])])
    const themes = new Set<string>(isDebugPreview ? THEMES.map((theme) => theme.id) : [...DEFAULT_UNLOCKS.themes, ...(profile?.unlockedThemeIds ?? [])])
    const backgrounds = new Set<string>(isDebugPreview ? BACKGROUNDS.map((background) => background.id) : [...DEFAULT_UNLOCKS.backgrounds, ...(profile?.unlockedBackgroundIds ?? [])])
    const orbitSkins = new Set<string>(isDebugPreview ? ORBIT_SKINS.map((skin) => skin.id) : [...(DEFAULT_UNLOCKS.orbitSkins ?? ['orbit_cream']), ...(profile?.unlockedOrbitSkinIds ?? [])])
    const prefixes = new Set<string>(isDebugPreview ? TITLE_PREFIXES : [...DEFAULT_UNLOCKS.titlePrefixes, ...(profile?.unlockedTitlePrefixes ?? [])])
    const suffixes = new Set<string>(isDebugPreview ? TITLE_SUFFIXES : [...DEFAULT_UNLOCKS.titleSuffixes, ...(profile?.unlockedTitleSuffixes ?? [])])
    const audio = new Set<string>(isDebugPreview ? AUDIO_TRACKS.map((track) => track.id) : DEFAULT_UNLOCKS.audio)
    AVATARS.forEach((avatar) => {
      if (avatar.guardianId && avatars.has(avatar.id)) {
        audio.add(FOCUS_GUARDIANS[avatar.guardianId].audioId)
      }
    })
    return { avatars, themes, backgrounds, orbitSkins, prefixes, suffixes, audio }
  }, [isDebugPreview, profile])

  useEffect(() => {
    if (!highlightTarget || isLoading || !profile) return
    const el = itemRefs.current.get(highlightTarget.cosmeticId)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setHighlightedId(highlightTarget.cosmeticId)
      const pulseTimer = window.setTimeout(() => setHighlightedId(null), 2000)
      onHighlightConsumed?.()
      return () => window.clearTimeout(pulseTimer)
    }
    onHighlightConsumed?.()
  }, [highlightTarget, isLoading, profile, onHighlightConsumed])

  const run = async (id: string, action: () => Promise<void>) => {
    setBusyId(id)
    try {
      await action()
    } catch (error) {
      console.warn('[CharacterPage] action failed', error)
    } finally {
      setBusyId(null)
    }
  }

  const equipAvatar = (avatar: AvatarItem) =>
    run(avatar.id, () =>
      equip(
        avatar.guardianId
          ? { equippedAvatarId: avatar.id, equippedAudioId: FOCUS_GUARDIANS[avatar.guardianId].audioId }
          : { equippedAvatarId: avatar.id },
      ),
    )

  const initialTitle = parseEquippedTitle(profile?.equippedTitle)
  const [titlePrefix, setTitlePrefix] = useState<string>(() => initialTitle.prefix)
  const [titleSuffix, setTitleSuffix] = useState<string>(() => initialTitle.suffix)

  useEffect(() => {
    const nextTitle = parseEquippedTitle(profile?.equippedTitle)
    setTitlePrefix(nextTitle.prefix)
    setTitleSuffix(nextTitle.suffix)
  }, [profile?.equippedTitle])

  if (isLoading || !profile) {
    return <div className="gTabBody charV2"><p className="gMuted gPad">{t('NLSExperienceLoadingVault')}</p></div>
  }

  const equippedAvatar = AVATARS.find((avatar) => avatar.id === profile.equippedAvatarId)
  const equippedTheme = THEMES.find((theme) => theme.id === profile.equippedThemeId)
  const equippedBackground = BACKGROUNDS.find((bg) => bg.id === profile.equippedCanvasBgId)
  const equippedAudio = AUDIO_TRACKS.find((audio) => audio.id === profile.equippedAudioId)

  return (
    <div className="gTabBody charV2">
      <IdentitySpotlight
        avatar={equippedAvatar}
        equippedTitle={profile.equippedTitle}
        level={level}
        themeName={equippedTheme?.name ?? t('NLSExperienceDefault')}
        backgroundName={equippedBackground?.name ?? t('NLSExperienceDefault')}
        audioName={equippedAudio?.name ?? t('NLSExperienceSilence')}
        t={t}
      />

      <motion.section
        className="charSection"
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="charSectionHead">
          <h2 className="gSectionTitle">{t('NLSExperienceGuardianSanctum')}</h2>
          <span className="charSectionSub">{t('NLSExperienceGuardianSanctumBody')}</span>
        </div>
        <div className="charGuardianGrid">
          {AVATARS.map((avatar) => {
            const isOwned = owned.avatars.has(avatar.id)
            const isEquipped = profile.equippedAvatarId === avatar.id
            const canUnlock = !isOwned && level >= RARITY_UNLOCK_LEVEL[avatar.rarity]
            const isPreviewingThis = activePreviewCosmeticId === avatar.id
            return (
              <GuardianCard
                key={avatar.id}
                avatar={avatar}
                isOwned={isOwned}
                isEquipped={isEquipped}
                canUnlock={canUnlock}
                isBusy={busyId === avatar.id}
                previewBusy={previewBusy}
                isPreviewingThis={isPreviewingThis}
                onEquip={() => void equipAvatar(avatar)}
                onUnlock={() => void run(avatar.id, () => unlock('unlockedAvatarIds', avatar.id))}
                onPreview={() => handlePreview(avatar, 'AVATAR')}
                t={t}
              />
            )
          })}
        </div>
      </motion.section>

      <motion.section
        className="charSection"
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="charSectionHead">
          <h2 className="gSectionTitle">{t('NLSExperienceEnvironmentChamber')}</h2>
          <span className="charSectionSub">{t('NLSExperienceEnvironmentChamberBody')}</span>
        </div>
        <div className="charSubSection">
          <span className="charSubLabel"><PaletteRoundedIcon fontSize="inherit" /> {t('NLSExperienceThemes')}</span>
          <div className="charSlotGrid">
            {THEMES.map((theme) => {
              const isOwned = owned.themes.has(theme.id)
              const isEquipped = profile.equippedThemeId === theme.id
              const canUnlock = !isOwned && level >= RARITY_UNLOCK_LEVEL[theme.rarity]
              const isPreviewingThis = activePreviewCosmeticId === theme.id
              return (
                <SlotCard
                  key={theme.id}
                  item={theme}
                  isOwned={isOwned}
                  isEquipped={isEquipped}
                  canUnlock={canUnlock}
                  isBusy={busyId === theme.id}
                  previewBusy={previewBusy}
                  isPreviewingThis={isPreviewingThis}
                  lockedLabel={`Lv ${RARITY_UNLOCK_LEVEL[theme.rarity]}`}
                  onEquip={() => void run(theme.id, () => equip({ equippedThemeId: theme.id }))}
                  onUnlock={() => void run(theme.id, () => unlock('unlockedThemeIds' as SlotUnlockField, theme.id))}
                  onPreview={() => handlePreview(theme, 'THEME')}
                  registerRef={registerItemRef}
                  isHighlighted={highlightedId === theme.id}
                  variant="theme"
                  t={t}
                />
              )
            })}
          </div>
        </div>
        <div className="charSubSection">
          <span className="charSubLabel"><ShieldMoonRoundedIcon fontSize="inherit" /> Orbit skins</span>
          <div className="charSlotGrid">
            {ORBIT_SKINS.map((skin) => {
              const isOwned = owned.orbitSkins.has(skin.id)
              const isEquipped = (profile.equippedOrbitSkinId || 'orbit_cream') === skin.id
              const canUnlock = !isOwned && level >= RARITY_UNLOCK_LEVEL[skin.rarity]
              const isPreviewingThis = activePreviewCosmeticId === skin.id
              return (
                <SlotCard
                  key={skin.id}
                  item={skin}
                  isOwned={isOwned}
                  isEquipped={isEquipped}
                  canUnlock={canUnlock}
                  isBusy={busyId === skin.id}
                  previewBusy={previewBusy}
                  isPreviewingThis={isPreviewingThis}
                  lockedLabel={`Lv ${RARITY_UNLOCK_LEVEL[skin.rarity]}`}
                  onEquip={() => void run(skin.id, () => equip({ equippedOrbitSkinId: skin.id }))}
                  onUnlock={() => void run(skin.id, () => unlock('unlockedOrbitSkinIds' as SlotUnlockField, skin.id))}
                  onPreview={() => handlePreview(skin, 'ORBIT')}
                  registerRef={registerItemRef}
                  isHighlighted={highlightedId === skin.id}
                  variant="orbit"
                  t={t}
                />
              )
            })}
          </div>
        </div>
        <div className="charSubSection">
          <span className="charSubLabel"><WallpaperRoundedIcon fontSize="inherit" /> {t('NLSExperienceCanvasBackgrounds')}</span>
          <div className="charSlotGrid">
            {BACKGROUNDS.map((bg) => {
              const isOwned = owned.backgrounds.has(bg.id)
              const isEquipped = profile.equippedCanvasBgId === bg.id
              const canUnlock = !isOwned && level >= RARITY_UNLOCK_LEVEL[bg.rarity]
              const isPreviewingThis = activePreviewCosmeticId === bg.id
              return (
                <SlotCard
                  key={bg.id}
                  item={bg}
                  isOwned={isOwned}
                  isEquipped={isEquipped}
                  canUnlock={canUnlock}
                  isBusy={busyId === bg.id}
                  previewBusy={previewBusy}
                  isPreviewingThis={isPreviewingThis}
                  lockedLabel={`Lv ${RARITY_UNLOCK_LEVEL[bg.rarity]}`}
                  onEquip={() => void run(bg.id, () => equip({ equippedCanvasBgId: bg.id }))}
                  onUnlock={() => void run(bg.id, () => unlock('unlockedBackgroundIds' as SlotUnlockField, bg.id))}
                  onPreview={() => handlePreview(bg, 'BACKGROUND')}
                  registerRef={registerItemRef}
                  isHighlighted={highlightedId === bg.id}
                  variant="background"
                  t={t}
                />
              )
            })}
          </div>
        </div>
      </motion.section>

      <motion.section
        className="charSection"
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="charSectionHead">
          <h2 className="gSectionTitle">{t('NLSExperienceSoundscapeVault')}</h2>
          <span className="charSectionSub">{t('NLSExperienceSoundscapeVaultBody')}</span>
        </div>
        <div className="charSlotGrid charSlotGrid--audio">
          {AUDIO_TRACKS.map((audio) => {
            const isOwned = owned.audio.has(audio.id)
            const isEquipped = profile.equippedAudioId === audio.id
            const isPreviewingThis = activePreviewCosmeticId === audio.id
            return (
              <SlotCard
                key={audio.id}
                item={audio}
                isOwned={isOwned}
                isEquipped={isEquipped}
                canUnlock={false}
                isBusy={busyId === audio.id}
                previewBusy={previewBusy}
                isPreviewingThis={isPreviewingThis}
                lockedLabel={t('NLSExperienceEquipGuardian')}
                onEquip={() => void run(audio.id, () => equip({ equippedAudioId: audio.id }))}
                onUnlock={() => {}}
                onPreview={() => handlePreview(audio, 'SOUNDSCAPE')}
                registerRef={registerItemRef}
                isHighlighted={highlightedId === audio.id}
                variant="audio"
                t={t}
              />
            )
          })}
        </div>
      </motion.section>

      <motion.section
        className="charSection"
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="charSectionHead">
          <h2 className="gSectionTitle">{t('NLSExperienceTitleForge')}</h2>
          <span className="charSectionSub">{t('NLSExperienceTitleForgeBody')}</span>
        </div>
        <TitleForge
          prefix={titlePrefix}
          suffix={titleSuffix}
          ownedPrefixes={owned.prefixes}
          ownedSuffixes={owned.suffixes}
          isBusy={busyId === 'title'}
          onPrefix={setTitlePrefix}
          onSuffix={setTitleSuffix}
          onEquip={() =>
            void run('title', () => equip({ equippedTitle: `${titlePrefix} ${titleSuffix}` }))
          }
          t={t}
        />
      </motion.section>
    </div>
  )
}
